// SPDX-License-Identifier: Apache-2.0 AND LicenseRef-Pachamama-Proprietary
// Copyright 2026 The Zyra Project
// Modifications copyright 2026 Pachamama Studios B.V. All rights reserved.

/**
 * Externally-hosted endpoint configuration.
 *
 * Terraviz is designed so each deployed node operates **independently**
 * of every other node. Each base is resolved here from a build-time
 * `VITE_*` env var. These are read at module load; Vite inlines
 * `import.meta.env.VITE_*` as string literals at build time, so each
 * export is effectively a compile-time constant in the shipped bundle.
 *
 * AYNI (owner rule, 7 October 2026): AYNI reads everything from its own
 * servers. TerraViz's Vimeo video proxy and `.srt` caption proxy (run by
 * the Zyra Project) went with the S3 catalog: the NOAA datasets, their
 * captions and the cloud texture are files in AYNI's library bucket
 * (`catalog.pachamama-studios.stream`), and the NASA GIBS, street and
 * terrain tiles come through AYNI's own routes (`/api/tile`, `/api/map`).
 */

/**
 * Public origin of AYNI's production deployment.
 *
 * AYNI: this was TerraViz's production origin, the host the desktop
 * (Tauri) app sent its `/api/...` calls to. The desktop app went on
 * 2026-10-06, and on the web every `/api/...` call is relative to the
 * page, so this is only what `getApiOrigin()` answers when nothing
 * overrides it (it marks this node's own host as a publisher source in
 * `downloadService`, and is the share-link fallback without a window).
 */
const DEFAULT_API_ORIGIN = 'https://vr.ayni.eu.com'

/**
 * Resolve the active API origin.
 *
 * Reads `VITE_API_ORIGIN` and normalises it to just
 * `<scheme>://<host>[:port]` via the URL constructor — anything past
 * the origin (path, query, fragment) is dropped, which matches the
 * variable's name and prevents a misconfigured
 * `https://staging.example.com/foo` from producing
 * `https://staging.example.com/foo/api/layers`. Non-URL or
 * non-http(s) values fall back to `DEFAULT_API_ORIGIN` rather than
 * throwing. `catalogSource` re-exports it for its existing callers.
 */
export function getApiOrigin(): string {
  const override = (import.meta.env.VITE_API_ORIGIN as string | undefined)?.trim()
  if (!override) return DEFAULT_API_ORIGIN
  try {
    const u = new URL(override)
    if (u.protocol !== 'http:' && u.protocol !== 'https:') return DEFAULT_API_ORIGIN
    return u.origin
  } catch {
    return DEFAULT_API_ORIGIN
  }
}

/** Trim a single trailing slash so callers can always append `/x`. */
function normalizeBase(value: string | undefined, fallback: string): string {
  const trimmed = value?.trim()
  if (!trimmed) return fallback
  return trimmed.replace(/\/+$/, '')
}

/**
 * Base for the Earth basemap textures used by the photoreal Earth
 * (VR + Orbit character) and the 2D globe overlays: diffuse, night
 * lights, normal map, and country-borders PNG. Consumers append
 * `/earth_diffuse_4096.jpg`, `/country-borders-black-8192.png`, etc.
 *
 * Same-origin by default. The eleven files are committed under
 * `public/assets/basemaps/`, so a build serves them from the node's
 * own domain with nothing configured, no install-time network, and
 * nothing that can be missing when the globe first paints.
 *
 * This used to default to upstream's CloudFront distribution, which
 * meant every fork's visitors pulled the Earth from upstream's
 * bandwidth unless its operator noticed Reference C and mirrored the
 * files by hand. Almost none did — it was the only entry in that
 * table applying to every node, and the only one with no tooling.
 *
 * Override with `VITE_EARTH_ASSET_BASE` to serve them from a CDN
 * instead — an optimisation now, not a workaround. `.gitattributes`
 * carries the note on why these are plain blobs rather than LFS.
 */
export const EARTH_ASSET_BASE = normalizeBase(
  import.meta.env.VITE_EARTH_ASSET_BASE,
  '/assets/basemaps',
)
